import { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNotifications } from '@fullstory/react-hooks';
import {
  makeUseAccess,
  makeUseCreateAccess,
  makeUseDispatchAccess,
} from '../../utils/accessControl';
import { SegmentRequestActions } from './actions';
import { getSegmentsAccess } from './selectors';
import { AppDispatch } from '../../types';
import { getOrgId } from '../fullstory/selectors';
import { generateRoutePath, SEGMENT_ROUTE } from '@fullstory/routes';
import { useRequestState } from '../../utils/hooks';
import { SetSegmentRequest } from '@fullstory/proto/fs/services/dx-objects';

export const useSegmentsAccess = makeUseAccess(SegmentRequestActions.getAccess, getSegmentsAccess);
export const useSegmentAccessDispatch = makeUseDispatchAccess(SegmentRequestActions.getAccess);

export const useSegmentsCreateAccess = makeUseCreateAccess(
  SegmentRequestActions.getAccess,
  getSegmentsAccess,
);

export const useFetchAllSegments = (orgId: string) => {
  const dispatch = useDispatch<AppDispatch>();

  const fetch = useCallback(() => {
    dispatch(SegmentRequestActions.getAllSegments(orgId));
  }, [dispatch, orgId]);

  const retry = useCallback(() => {
    dispatch(SegmentRequestActions.getAllSegments(orgId, true));
  }, [dispatch, orgId]);

  useEffect(() => fetch(), [fetch]);
  return {
    fetch,
    retry,
  };
};

export const useSegmentActions = () => {
  const notifications = useNotifications();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);

  const markViewed = useCallback(
    (segmentId: string) => dispatch(SegmentRequestActions.markSegmentViewed(orgId, segmentId)),
    [dispatch, orgId],
  );

  const create = useCallback(
    async (segment: Omit<SetSegmentRequest, 'orgId'>) => {
      try {
        const response = await dispatch(
          SegmentRequestActions.setSegment(orgId, segment.segment!, segment.visibility),
        );
        notifications?.success('Successfully created Segment');
        return response;
      } catch (e) {
        notifications?.error('Failed to create Segment');
        return null;
      }
    },
    [dispatch, notifications, orgId],
  );

  const update = useCallback(
    async (segment: Omit<SetSegmentRequest, 'orgId'>) => {
      try {
        const response = await dispatch(
          SegmentRequestActions.setSegment(orgId, segment.segment!, segment.visibility),
        );
        notifications?.success('Successfully created Segment');
        return response;
      } catch (e) {
        notifications?.error('Failed to create Segment');
        return null;
      }
    },
    [dispatch, notifications, orgId],
  );

  const star = useCallback(
    async (segmentId: string) => {
      try {
        await dispatch(SegmentRequestActions.starSegment(orgId, segmentId, true));
      } catch (e) {
        notifications?.error('Failed to star Segment');
      }
    },
    [dispatch, notifications, orgId],
  );

  const unstar = useCallback(
    async (segmentId: string) => {
      try {
        await dispatch(SegmentRequestActions.starSegment(orgId, segmentId, false));
      } catch (e) {
        notifications?.error('Failed to unstar Segment');
      }
    },
    [dispatch, notifications, orgId],
  );

  const archive = useCallback(
    async (segmentId: string) => {
      try {
        const response = await dispatch(
          SegmentRequestActions.archiveSegment(orgId, segmentId, true),
        );
        notifications?.success('Successfully archived Segment.');
        return response;
      } catch (e) {
        notifications?.error('Failed to archive Segment.');
        return null;
      }
    },
    [dispatch, notifications, orgId],
  );

  const restore = useCallback(
    async (segmentId: string) => {
      try {
        await dispatch(SegmentRequestActions.archiveSegment(orgId, segmentId, false));
        const unarchivedSegmentUrl = generateRoutePath(SEGMENT_ROUTE, {
          orgId,
          segmentId,
        });
        notifications?.successLink(
          'Successfully restored Segment. ',
          'See it now.',
          unarchivedSegmentUrl,
        );
      } catch (e) {
        notifications?.error('Failed to restore Segment.');
      }
    },
    [dispatch, notifications, orgId],
  );

  return {
    markViewed,
    create,
    update,
    star,
    unstar,
    archive,
    restore,
  };
};

export const useSegmentRequestState = () =>
  useRequestState(state => state.segments.allSegmentsRequest);
